import type { PublicView } from '@ringmaster/shared';
import type { RoleId } from '@ringmaster/shared';
import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { roleAccent } from './roleGlyph.js';
import plaqueCracked from '../assets/ceremony/plaque-cracked.webp';
import plaqueClean from '../assets/ceremony/plaque-clean.webp';
import clawGouge from '../assets/ceremony/claw-gouge.webp';

/**
 * DawnScreen — the dawn reveal for LIVING players + the host view (dead players
 * get the blood-red DeadScreen instead).
 *
 * SACRED RULE: only the dead player's phone turns red. This screen stays a calm
 * near-black with a faint ash-rose bleed from the top — never a blood flash.
 *
 * A death names the victim on a cracked, claw-gouged plaque. A quiet night shows
 * a clean plaque, the dawn-bird glyph, and a brief wash of gold dust. The
 * narration caption rides the shared NarrationBanner (rendered by HandGameView).
 */
export function DawnScreen({ publicView }: { publicView: PublicView }) {
  const dawn = publicView.dawn;
  const victim = dawn?.victimId ? publicView.players.find((p) => p.id === dawn.victimId) : null;
  const revealedRole = victim?.revealedRole as RoleId | undefined;

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        // Calm near-black. NOT the blood flash — that is reserved for the dead.
        background: 'linear-gradient(180deg, #080808 0%, #0d0507 100%)',
        gap: 'var(--space-5)',
        padding: 'var(--space-5)',
        overflow: 'hidden',
      }}
    >
      <DawnStyles />

      {/* Ash-rose light creeping in from the top ~10% — first light, not blood. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '38%',
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg, rgba(193,102,60,0.22) 0%, rgba(125,59,51,0.12) 45%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/* Quiet night → a brief, gentle gold-dust wash (phone-tier capped). */}
      {!victim && <ParticleLayer preset="goldDust" intensity={0.5} slot={0} />}

      {victim ? (
        <div className="dawn-appear" style={{ position: 'relative', textAlign: 'center', width: '100%', maxWidth: 340 }}>
          <p className="dawn-eyebrow">Dawn breaks</p>

          {/* Victim named on a cracked, claw-gouged plaque */}
          <div className="dawn-plaque">
            <img src={plaqueCracked} alt="" aria-hidden className="dawn-plaque-bg" />
            <img src={clawGouge} alt="" aria-hidden className="dawn-plaque-gouge" />
            <div className="dawn-plaque-body">
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 30, margin: 0, color: '#fff' }}>{victim.name}</p>
              <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 15, margin: 'var(--space-2) 0 0' }}>
                was found dead in the night
              </p>
              {revealedRole && (
                <p
                  style={{
                    marginTop: 'var(--space-3)',
                    fontFamily: 'var(--font-display)',
                    fontSize: 17,
                    color: roleAccent(revealedRole),
                    fontWeight: 600,
                  }}
                >
                  They were a {revealedRole.charAt(0) + revealedRole.slice(1).toLowerCase()}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="dawn-appear" style={{ position: 'relative', textAlign: 'center', width: '100%', maxWidth: 340 }}>
          <p className="dawn-eyebrow">Dawn breaks</p>
          <div className="dawn-plaque">
            <img src={plaqueClean} alt="" aria-hidden className="dawn-plaque-bg" />
            <div className="dawn-plaque-body" style={{ paddingTop: 'var(--space-5)', paddingBottom: 'var(--space-5)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
                <Glyph name="dawnBird" size={44} tone="gold" />
              </div>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: 0, color: '#fff' }}>
                Everyone survived the night
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DawnStyles() {
  return (
    <style>{`
      .dawn-eyebrow {
        font-family: var(--font-display); font-size: 14px; text-transform: uppercase;
        letter-spacing: 0.15em; color: rgba(255,255,255,0.6);
        margin: 0 0 var(--space-4);
      }
      .dawn-plaque { position: relative; width: 100%; border-radius: var(--radius-card); overflow: hidden;
        border: 1px solid var(--surface-stroke); box-shadow: var(--shadow-card); }
      .dawn-plaque-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.5; }
      .dawn-plaque-gouge { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
        opacity: 0.7; mix-blend-mode: screen; pointer-events: none; }
      .dawn-plaque-body { position: relative; z-index: 1; padding: var(--space-6) var(--space-5);
        background: linear-gradient(180deg, rgba(8,8,8,0.34) 0%, rgba(8,8,8,0.62) 100%); }
      .dawn-appear { animation: dawn-appear 0.6s var(--ease-standard); }
      @keyframes dawn-appear { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      @media (prefers-reduced-motion: reduce) { .dawn-appear { animation: none; } }
    `}</style>
  );
}
